import React from 'react';
import Image from '../../../components/AppImage';

const ArticleContent = () => {
  return (
    <section id="article-content" className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Featured Image */}
        <div className="mb-12">
          <Image
            src="https://img.rocket.new/generatedImages/rocket_gen_img_197d260f2-1769391961985.png"
            alt="Interface moderne d'intelligence artificielle dans l'industrie automobile avec des écrans holographiques et des données en temps réel"
            className="w-full h-96 object-cover rounded-2xl shadow-lg" />

          <p className="text-gray-500 text-sm mt-3 text-center italic">
            L'IA transforme l'expérience automobile avec des interfaces intelligentes et des données en temps réel
          </p>
        </div>

        {/* Article Content */}
        <div className="prose prose-lg max-w-none">
          {/* Introduction */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">L'Avènement de l'IA dans l'Industrie Automobile Africaine</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              L'industrie automobile traverse une révolution technologique sans précédent. En Afrique, cette transformation 
              représente une opportunité unique de propulser le secteur vers l'excellence mondiale. L'Intelligence Artificielle 
              n'est plus un concept futuriste, mais une réalité qui redéfinit chaque aspect de l'expérience automobile.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              De la maintenance prédictive à l'optimisation de la chaîne d'approvisionnement, l'IA offre des solutions 
              concrètes aux défis spécifiques du marché africain. Cette transformation va bien au-delà de la simple 
              automatisation : elle crée de nouveaux modèles économiques et redéfinit la relation entre les constructeurs, 
              les concessionnaires et les consommateurs.
            </p>
          </div>

          {/* Key Benefits Section */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Les Piliers de la Transformation IA Automobile</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Maintenance Prédictive</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Anticipez les pannes avant qu'elles ne surviennent. Réduisez les coûts de maintenance de 30% 
                  et augmentez la satisfaction client grâce à une disponibilité optimale des véhicules.
                </p>
              </div>

              <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Personnalisation Client</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Créez des expériences sur-mesure basées sur les préférences et comportements. 
                  Augmentez les ventes de 25% avec des recommandations intelligentes et personnalisées.
                </p>
              </div>

              <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Optimisation Opérationnelle</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Streamline vos processus avec l'IA. Réduisez les délais de livraison de 40% et 
                  optimisez la gestion des stocks pour minimiser les coûts.
                </p>
              </div>

              <div className="bg-orange-50 rounded-xl p-6 border border-orange-200">
                <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Analyse Prédictive</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Anticipez les tendances du marché et les besoins clients. Prenez des décisions 
                  éclairées basées sur des données précises et des projections fiables.
                </p>
              </div>
            </div>
          </div>

          {/* Implementation Strategy */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Stratégie d'Implémentation IA : Le Modèle CRF PERFECTION</h2>
            
            <div className="bg-gradient-to-r from-purple-100 to-blue-100 rounded-2xl p-8 mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Notre Approche en 4 Phases</h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm">1</div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Évaluation & Diagnostic</h4>
                    <p className="text-gray-700 text-sm">Audit complet de vos processus actuels et identification des opportunités IA à fort impact.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">2</div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Conception & Prototypage</h4>
                    <p className="text-gray-700 text-sm">Développement de solutions IA sur-mesure adaptées à votre contexte et vos contraintes.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm">3</div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Déploiement Progressif</h4>
                    <p className="text-gray-700 text-sm">Implémentation par phases avec formation des équipes et mesure continue des performances.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold text-sm">4</div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Optimisation Continue</h4>
                    <p className="text-gray-700 text-sm">Monitoring, ajustements et évolution des solutions pour garantir un ROI optimal.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Impact Metrics */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Résultats Mesurables</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="text-center bg-gray-50 rounded-xl p-6">
                <div className="text-3xl font-bold text-purple-600 mb-2">85%</div>
                <div className="text-gray-700 text-sm">Amélioration de la satisfaction client</div>
              </div>
              <div className="text-center bg-gray-50 rounded-xl p-6">
                <div className="text-3xl font-bold text-blue-600 mb-2">60%</div>
                <div className="text-gray-700 text-sm">Réduction des coûts opérationnels</div>
              </div>
              <div className="text-center bg-gray-50 rounded-xl p-6">
                <div className="text-3xl font-bold text-green-600 mb-2">150%</div>
                <div className="text-gray-700 text-sm">Augmentation du ROI sur 2 ans</div>
              </div>
            </div>
          </div>

          {/* Conclusion */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">L'Avenir de l'Automobile Africaine est Intelligent</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              L'Intelligence Artificielle n'est plus une option dans l'industrie automobile - c'est une nécessité stratégique. 
              Les entreprises qui embrassent cette transformation aujourd'hui prennent une avance décisive sur leurs concurrents 
              et positionnent l'Afrique comme un acteur majeur de l'innovation automobile mondiale.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Chez CRF PERFECTION, nous accompagnons cette révolution en offrant notre expertise technique et notre 
              compréhension profonde du marché africain. Ensemble, construisons l'avenir de l'excellence automobile avec l'Intelligence Artificielle.
            </p>
          </div>
        </div>

        {/* Article Tags */}
        <div className="border-t border-gray-200 pt-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Tags de l'Article</h3>
          <div className="flex flex-wrap gap-3">
            {['Intelligence Artificielle', 'Automobile', 'Transformation Digitale', 'Maintenance Prédictive', 'Optimisation', 'IA Africaine', 'Innovation']?.map((tag, index) =>
            <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-purple-100 hover:text-purple-700 transition-colors duration-200 cursor-pointer">
                #{tag}
              </span>
            )}
          </div>
        </div>
      </div>
    </section>);

};

export default ArticleContent;